import Model from './Model';
import { JsonApiCollection, JsonApiCollectionMeta, NewableCollection, NewableModel } from './types';
import deepFreeze from 'deep-freeze';

export default abstract class Collection<T extends Model> {
  private items: T[];
  private _data?: JsonApiCollection;

  constructor(_data?: JsonApiCollection) {
    this._data = _data ? deepFreeze(JSON.parse(JSON.stringify(_data))) : _data;
    this.items = this.getItems();
  }

  getItemKeys(): number[] {
    if (!this._data || !this._data.data) return [];
    return (Object.keys(this._data.data) as any[]) as number[];
  }

  getItems(): T[] {
    return this.getItemKeys().map(key => this.getItem(key) as T);
  }

  toArray(): T[] {
    return this.getItems();
  }

  getItem(key: number | number): T | undefined {
    if (!this._data || !this._data.data || !this._data.data[key]) {
      return;
    }
    return new (this.getModel())(this._data.data[key]) as T;
  }

  // tslint:disable-next-line: function-name
  *[Symbol.iterator](): IterableIterator<T> {
    for (const i of this.getItemKeys()) {
      yield this.getItem(i) as T;
    }
  }

  getModel(): NewableModel<T> {
    return this.getCollection().getModel();
  }

  static getModel(): NewableModel<Model> {
    throw new Error('You have to implement the static method getModel');
  }

  getType(): string {
    return this.getCollection().getType();
  }

  static getType(): string {
    return this.getModel().getType();
  }

  getCollection(): NewableCollection<T> {
    return (this as any).constructor;
  }

  static getCollection(): NewableCollection<Model> {
    return this as any;
  }

  raw(): JsonApiCollection | undefined {
    return JSON.parse(JSON.stringify(this._data));
  }

  clone(): Collection<T> {
    return new (this.getCollection())(this.raw());
  }

  jsonSerialize(): JsonApiCollection {
    return {
      data: this.items.map(item => item.jsonSerialize()),
    };
  }

  toJSON() {
    return this.items.map(item => item.toJSON());
  }

  map(callbackfn: (value: T, index: number, array: T[]) => any, thisArg?: any): any[] {
    return this.items.map(callbackfn, thisArg);
  }

  reduce(
    callbackfn: (previousValue: any, currentValue: any, currentIndex: number, array: any[]) => any,
    initialValue: any,
  ): any {
    return this.items.reduce(callbackfn, initialValue);
  }

  filter(callbackfn: (value: T, index: number, array: T[]) => any, thisArg?: any): Collection<T> {
    return new (this.getCollection())({
      data: this.items.filter(callbackfn, thisArg).map(model => model.jsonSerialize()),
    });
  }

  forEach(callbackfn: (value: T, index: number, array: T[]) => void, thisArg?: any) {
    return this.items.forEach(callbackfn, thisArg);
  }

  find(predicate: (value: T, index: number, obj: T[]) => unknown, thisArg?: any): T | undefined {
    return this.items.find(predicate, thisArg);
  }

  findIndex(predicate: (value: T, index: number, obj: T[]) => unknown, thisArg?: any): number {
    return this.items.findIndex(predicate, thisArg);
  }

  slice(start?: number, end?: number): Collection<T> {
    return new (this.getCollection())({
      data: this.items.slice(start, end).map(model => model.jsonSerialize()),
    });
  }

  some(callbackfn: (value: T, index: number, array: T[]) => unknown, thisArg?: any): boolean {
    return this.items.some(callbackfn, thisArg);
  }

  every(callbackfn: (value: T, index: number, array: T[]) => unknown, thisArg?: any): boolean {
    return this.items.every(callbackfn, thisArg);
  }

  sort(compareFn?: (a: T, b: T) => number): Collection<T> {
    return new (this.getCollection())({
      data: this.items.sort(compareFn).map(model => model.jsonSerialize()),
    });
  }

  get length() {
    return this.items.length;
  }

  getMeta(): JsonApiCollectionMeta | undefined {
    if (!this._data || !this._data.meta) return;
    return this._data.meta;
  }
}
